import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useAnnotation from 'hooks/useAnnotation'
import { AnnotationType, BuildingFile } from 'types/building'

import Button from 'components/atoms/Button'
import Modal from 'components/atoms/Modal'
import Title from 'components/atoms/Title'

import AnnotationSetting from './AnnotationSetting'
import AnnotationTypeSelector from './AnnotationTypeSelector'
import ConfirmChangeAnnotation from './ConfirmChangeAnnotation'
import FileList from './FileList'
import MemoSetting from './MemoSetting'
import ModalDeleteAnnotation from './ModalDeleteAnnotation'

export default function AnnotationModal() {
  const { t } = useTranslation()
  const [isAddingFile, setIsAddingFile] = useState(false)
  const [isChangeType, setIsChangeType] = useState(false)
  const [openModal, setOpenModal] = useState<string>('')
  const [existsFile, setExistsFile] = useState<BuildingFile[]>([])
  const [selectedFiles, setSelectedFiles] = useState<BuildingFile[]>([
    ...existsFile
  ])

  const {
    form,
    isLoading,
    isDataExistsAnnotation,
    dataSource,
    quill,
    quillRef,
    QuillClass,
    currentSelected,
    setCurrentSelected,
    childrenFolder,
    selectedFolder,
    setSelectedFolder,
    selectedChildrenFolder,
    setSelectedChildrenFolder,
    handleBack,
    handleCloseModal,
    handleSubmit
  } = useAnnotation()

  const changeSelectedFolder = (value: any) => {
    if (value !== selectedFolder) {
      setCurrentSelected((prev) => ({
        ...prev,
        type: value
      }))
      if (!isDataExistsAnnotation) {
        setIsChangeType(true)
      } else {
        setSelectedFolder(value)
      }
    }
  }

  const handleCloseFileSelectMenu = (clear?: boolean) => {
    if (clear) {
      setSelectedFiles([])
    } else {
      const filesPrev =
        currentSelected?.files?.filter(
          (file) => !existsFile.map((item) => item.id).includes(file.id)
        ) || []
      setCurrentSelected((prev) => ({
        ...prev,
        files: [...filesPrev, ...selectedFiles]
      }))
    }
    setIsAddingFile(false)
  }

  const handleSelectFile = (file: BuildingFile) => {
    const index = selectedFiles.findIndex((item) => item.id === file.id)
    if (index === -1) {
      setSelectedFiles((prev) => [
        ...prev,
        { ...file, category: selectedChildrenFolder?.id }
      ])
      return
    }
    setSelectedFiles((prev) => prev.filter((_, i) => index !== i))
  }

  useEffect(() => {
    if (existsFile.length) {
      setSelectedFiles([...existsFile])
    }
  }, [existsFile])

  useEffect(() => {
    // custom placeholder of popup link
    const tooltipInput = document.querySelector('.ql-tooltip input[data-link]')
    if (tooltipInput) {
      tooltipInput.setAttribute('data-link', 'https://')
      tooltipInput.setAttribute('placeholder', 'https://')
    }
  }, [currentSelected, selectedFolder])

  return (
    <>
      <Modal width="840px" rootClassName="shared-permission-modal">
        {isAddingFile ? (
          <FileList
            folder={dataSource}
            selectedChildrenFolder={selectedChildrenFolder}
            selectedFiles={selectedFiles}
            handleSelectFile={handleSelectFile}
            handleCloseFileSelectMenu={handleCloseFileSelectMenu}
          />
        ) : (
          <>
            <div className="flex flex-col w-[45rem]">
              <Title
                className="mb-[10px]"
                height={28.96}
                title={t('building.titleAddAnnotation')}
              />

              <AnnotationTypeSelector
                changeSelectedFolder={changeSelectedFolder}
                selectedChildrenFolder={selectedChildrenFolder}
                selectedFolder={selectedFolder}
                t={t}
              />

              {selectedFolder === AnnotationType.MEMO ? (
                <MemoSetting
                  quill={quill}
                  quillRef={quillRef}
                  QuillClass={QuillClass}
                  memo={currentSelected?.memo}
                />
              ) : (
                <AnnotationSetting
                  form={form}
                  selectedFolder={selectedFolder}
                  selectedChildrenFolder={selectedChildrenFolder}
                  childrenFolder={childrenFolder}
                  dataSource={dataSource}
                  setSelectedChildrenFolder={setSelectedChildrenFolder}
                  handleBack={handleBack}
                  setExistsFile={setExistsFile}
                  setIsAddingFile={setIsAddingFile}
                />
              )}
            </div>

            <div className="flex w-full items-end pt-5 pl-5 pr-[30px] mt-[38px] justify-between border-0 border-t border-solid border-[#EEEFF2] mb-5">
              {currentSelected?.id && (
                <Button
                  onClick={() => setOpenModal('deleted')}
                  sizing="w-[140px] h-[50px]"
                  className="bg-[#B80808]"
                >
                  {t('common.delete')}
                </Button>
              )}

              <div className="flex gap-10 ml-auto">
                <Button sizing="w-[140px] h-[50px]" onClick={handleCloseModal}>
                  {t('common.cancel')}
                </Button>
                <Button
                  sizing="w-[140px] h-[50px]"
                  className="theme-bg-primary"
                  loading={isLoading}
                  onClick={handleSubmit}
                >
                  {t('common.confirm')}
                </Button>
              </div>
            </div>
          </>
        )}
      </Modal>
      {openModal === 'deleted' && (
        <ModalDeleteAnnotation
          setIsOpen={setOpenModal}
          handleCloseModal={handleCloseModal}
        />
      )}
      {isChangeType && (
        <ConfirmChangeAnnotation
          selectedFolder={selectedFolder}
          setSelectedFolder={setSelectedFolder}
          setIsOpenConfirm={setIsChangeType}
        />
      )}
    </>
  )
}
